import h from 'hyperscript';

import type { ChangeEvent } from '@disy/lib/event-util';
import type { Switch } from '@disy/ui/switch';
import { getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';

export function createDataViewAttributeModificationCommand(
  dataViewId: DataViewId,
  attributeId: ActiveAttributeId,
  modificationType: AttributeModificationKey,
  value: string | null | undefined,
): Command {
  return value === null || value === undefined ?
      Command.REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION({
        dataViewId,
        attributeId,
        modificationType,
      })
    : Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION({
        dataViewId,
        attributeId,
        modificationType,
        value,
      });
}

function createWorkbookAttributeModificationCommand(
  attributeId: AttributeId,
  modificationType: AttributeModificationKey,
  value: string | null | undefined,
): Command {
  return value === null || value === undefined ?
      Command.REMOVE_WORKBOOK_ATTRIBUTE_MODIFICATION({
        attributeId,
        modificationType,
      })
    : Command.SET_WORKBOOK_ATTRIBUTE_MODIFICATION({
        attributeId,
        modificationType,
        value,
      });
}

export function createAttributeModificationSwitch(
  attribute: WorkbookAttributeDto | undefined,
  dataViewId: DataViewId,
  key: AttributeModificationKey,
) {
  if (!attribute) {
    return h<Switch>('d-switch', { checked: false, disabled: true, slot: 'summary-aside' });
  }
  const switcher = h<Switch>('d-switch', {
    checked: getSettingOrSettingDefault(attribute, key),
    onchange: (e: ChangeEvent) => {
      e.stopPropagation();
      const attributeModificationCommand = createDataViewAttributeModificationCommand(
        dataViewId,
        attribute.id as ActiveAttributeId,
        key,
        switcher.checked.toString(),
      );
      getStore().dispatch(executeCommand(attributeModificationCommand));
    },
  });
  return switcher;
}

export function modifyWorkbookAttribute(
  attributeId: AttributeId,
  modification: AttributeModificationKey | [AttributeModificationKey, string | null][],
  value: string | null = null,
) {
  if (Array.isArray(modification)) {
    // batch modification
    return getStore().dispatch(
      executeCommand(
        Command.COMPOSITE_COMMAND({
          commands: modification.map(([mod, val]) => {
            return createWorkbookAttributeModificationCommand(attributeId, mod, val);
          }),
        }),
      ),
    );
  }
  return getStore().dispatch(
    executeCommand(createWorkbookAttributeModificationCommand(attributeId, modification, value)),
  );
}
